import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { SUPPORTED_CURRENCIES, resolveCurrency } from '../utils/fx';
import { GEO_CURRENCY_KEY } from '../i18n';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const STORAGE_KEY = 'troly_currency';

const Ctx = createContext(null);

export function CurrencyProvider({ children }) {
  const { i18n } = useTranslation();
  const [rates, setRates] = useState(null);          // { [code]: rate }
  const [base, setBase]   = useState('USD');
  const [fetchedAt, setFetchedAt] = useState(null);

  // Prioridad de la divisa por defecto:
  //   1. Elección manual del usuario (troly_currency) — siempre gana.
  //   2. Divisa del país detectado por geo-IP (troly_currency_geo), persistida
  //      por detectLanguageByGeo ANTES del primer render.
  //   3. Divisa según el idioma activo (México-first para español).
  const [displayCurrency, setDisplayCurrency] = useState(() => {
    const read = (k) => (typeof localStorage !== 'undefined' ? localStorage.getItem(k) : null);
    const manual = read(STORAGE_KEY);
    if (manual && SUPPORTED_CURRENCIES.includes(manual)) return manual;
    const geo = read(GEO_CURRENCY_KEY);
    if (geo && SUPPORTED_CURRENCIES.includes(geo)) return geo;
    return resolveCurrency({ lang: i18n?.language });
  });

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/api/fx`).then(({ data }) => {
      if (cancelled) return;
      setRates(data?.rates || null);
      setBase(data?.base || 'USD');
      setFetchedAt(data?.fetchedAt || null);
    }).catch(() => { /* sin rates: Price renderiza divisa original */ });
    return () => { cancelled = true; };
  }, []);

  const changeCurrency = useCallback((code) => {
    if (!SUPPORTED_CURRENCIES.includes(code)) return;
    setDisplayCurrency(code);
    try { localStorage.setItem(STORAGE_KEY, code); } catch { /* ignore */ }
  }, []);

  const value = {
    rates, base, fetchedAt,
    displayCurrency,
    changeCurrency,
    supported: SUPPORTED_CURRENCIES,
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useCurrency = () => useContext(Ctx) || {
  // Fallback seguro cuando algún componente se renderiza fuera del provider
  // (tests, errores). Mantiene la UI viva sin convertir.
  rates: null, base: 'USD', displayCurrency: 'USD',
  changeCurrency: () => {}, supported: SUPPORTED_CURRENCIES,
};
